import moment from 'moment'
import qs from 'qs'
import { PASSENGER_TYPES } from 'tickets/utils/constants'
import { zipWith, contains } from 'common/utils/arrayUtils'
import { dateFormat } from 'common/utils/time'
import { createPassenger, cleanUpPassenger } from 'tickets/utils/passengers.js'
import { DEFAULT_LANGUAGE } from 'common/utils/constants'
import { ticketsPath } from 'common/utils/paths'
import history from 'common/utils/history'

const knownQueryParams = [
  'oDateTime',
  'iDateTime',
  'adults',
  'seniors',
  'youths',
  'ages',
  'cards',
  'flags',
  'mode',
]

// List of params that are not used for deep linking for now
const unsupportedStartupParams = [
  'oid',
  'iid',
  'modalType',
]

export const buildUrl = (
  originCanonical,
  destinationCanonical,
  oDateTime,
  iDateTime,
  pax,
  debugFlags,
  languageCode,
  mode,
) => {
  const path = getTicketsPath(languageCode, originCanonical, destinationCanonical)
  const queryObject = {}

  const oDateTimeString = oDateTime.format(dateFormat)
  queryObject.oDateTime = oDateTimeString
  if (iDateTime) {
    const iDateTimeString = iDateTime.format(dateFormat)
    queryObject.iDateTime = iDateTimeString
  }

  if (debugFlags) {
    queryObject.flags = debugFlags
  }

  // order: adults, seniors, youths
  const paxOrder = {
    adult: 1,
    senior: 2,
    youth: 3,
  }

  pax.sort((a, b) => paxOrder[a.type] - paxOrder[b.type])

  const ages = []
  const cards = []

  pax.forEach((passenger) => {
    if (passenger.type == PASSENGER_TYPES.adult) {
      queryObject.adults = queryObject.adults ? queryObject.adults + 1 : 1
    } else if (passenger.type == PASSENGER_TYPES.senior) {
      queryObject.seniors = queryObject.seniors ? queryObject.seniors + 1 : 1
    } else if (passenger.type == PASSENGER_TYPES.youth) {
      queryObject.youths = queryObject.youths ? queryObject.youths + 1 : 1
    }
    ages.push(passenger.age)
    cards.push(passenger.travelCard)
  })

  if (mustAddToUrl(ages)) {
    queryObject.ages = ages.join(',')
  }
  if (mustAddToUrl(cards)) {
    queryObject.cards = cards.join(',')
  }

  if (mode) {
    queryObject.mode = mode
  }

  // Append any additional querystring fields that we aren't aware of (just echo them back)
  addUnknownQueryParams(queryObject)

  // Removing params we can't deeplink to for now
  cleanUpUnsupportedQuery(queryObject)

  const queryString = Object.keys(queryObject).map(key =>
    `${key}=${encodeURIComponent(queryObject[key]).replace('%7C', '')}` // remove the pipe character as it causes issues with the referer url validation
  ).join('&')

  return `${path}?${queryString}`
}

export const getTicketsPath = (languageCode, originCanonical, destinationCanonical) => {
  const languagePart = getLanguagePathPart(languageCode)
  return `${languagePart}${ticketsPath}/${originCanonical}/${destinationCanonical}`
}

const cleanUpUnsupportedQuery = (queryObject) => {
  Object.keys(queryObject).forEach(param => {
    // If we don't know about the field, add its value to the object
    if (unsupportedStartupParams.find(knownParam => knownParam == param)) {
      delete queryObject[param]
    }
  })
}

const addUnknownQueryParams = (queryObject) => {
  const unknownParams = getUnknownQueryParams()
  Object.keys(unknownParams).forEach(param => {
    queryObject[param] = unknownParams[param]
  })
}

const mustAddToUrl = (array) => {
  // if all values would be null, don't have to add to url
  return contains(array, (el) => !!el)
}

export const getUnknownQueryParams = () => {
  const queryStringParams = getParamsFromUrl()

  const unknownParams = {}
  Object.keys(queryStringParams).forEach(param => {
    // If we don't know about the field, add its value to the object
    if (!knownQueryParams.find(knownParam => knownParam == param)) {
      unknownParams[param] = queryStringParams[param]
    }
  })

  return unknownParams
}

export const cleanUpUrl = (originCanonical, destinationCanonical, languageCode) => {
  const oDateTime = getCleanOutboundDateFromUrl()
  const iDateTime = getCleanInboundDateFromUrl()
  const pax = getCleanPaxFromUrl()
  const debugFlags = getDebugFlagsFromUrl()

  const url = buildUrl(
    originCanonical,
    destinationCanonical,
    moment(oDateTime),
    iDateTime ? moment(iDateTime) : null,
    pax,
    debugFlags,
    languageCode,
    getModeFromUrl(),
  )

  history.replace(url)
}

export const getCleanPaxFromUrl = () => {
  const params = getParamsFromUrl()

  const adults = params.adults ? Array(Number(params.adults)).fill(PASSENGER_TYPES.adult) : []
  const seniors = params.seniors ? Array(Number(params.seniors)).fill(PASSENGER_TYPES.senior) : []
  const youths = params.youths ? Array(Number(params.youths)).fill(PASSENGER_TYPES.youth) : []
  let types = adults.concat(seniors).concat(youths)

  // if no passengers specified, default to one adult
  if (types.length == 0) {
    types = [PASSENGER_TYPES.adult]
  }

  const cards = params.cards ? params.cards.split(',') : Array(types.length)
  const paxAges = params.ages ? params.ages.split(',') : Array(types.length)

  return zipWith(
    [types, cards, paxAges],
    (type, travelCard, age) => {
      const passenger = createPassenger(type, age, travelCard)
      return cleanUpPassenger(passenger)
    }
  )
}

export const getCleanOutboundDateFromUrl = () => {
  const params = getParamsFromUrl()
  return cleanUpDate(params.oDateTime)
}

export const getCleanInboundDateFromUrl = () => {
  const params = getParamsFromUrl()
  return params.iDateTime ? cleanUpDate(params.iDateTime) : null
}

export const getModeFromUrl = () => {
  const paramsObj = getParamsFromUrl()
  return paramsObj.mode
}

export const getRequestIdUrl = () => {
  const paramsObj = getParamsFromUrl()
  return paramsObj.requestId
}

export const getOutboundTimeFromUrl = () => getTimeFromUrl(true)

export const getInboundTimeFromUrl = () => getTimeFromUrl(false)

const getLanguagePathPart = (languageCode) => {
  if (!!languageCode && languageCode.toLowerCase() != DEFAULT_LANGUAGE) {
    return `/${languageCode}`
  }
  return ''
}

const getTimeFromUrl = (isOutbound) => {
  const paramsObj = getParamsFromUrl()
  const dateTime = isOutbound ? paramsObj.oDateTime : paramsObj.iDateTime
  const timeIsSpecified = dateTime && dateTime.indexOf(':') >= 0

  if (!dateTime || !timeIsSpecified) {
    return null
  }

  return Number(moment(dateTime).format('H'))
}

export const getDebugFlagsFromUrl = () => {
  const paramsObj = getParamsFromUrl()
  return paramsObj.flags
}

const getParamsFromUrl = () => {
  return qs.parse(window.location.search, { depth: 1, ignoreQueryPrefix: true })
}

const cleanUpDate = (date) => {
  // if no valid date specified, default to user's "today"
  const cleanDate = isValidDate(date) ? moment(date) : moment()
  return cleanDate.format(dateFormat)
}

const isValidDate = (date) => {
  // fast out if no date supplied
  if (!date) {
    return false
  }

  const momentDate = moment(date, dateFormat)
  const today = moment().startOf('day')

  // valid if we can parse the moment & it is today or in the future
  return momentDate.isValid() && momentDate.isSameOrAfter(today)
}
